/**
 * Mall Map — WebGL scene.
 *
 * Imperative three.js scene, same shape as DigitalTwin/Twin3D/floorScene.js:
 * `createMallScene(container, opts)` returns an api object; React owns the
 * lifecycle and pushes state in through setters rather than re-creating.
 *
 *   const api = createMallScene(el, { onSelect, onHover })
 *   api.setFloor('GF'); api.setHeatmap('sales'); api.setRoute(route)
 *   api.dispose()
 *
 * The whole mall is modelled as a true floor stack at real heights. Floors
 * below the active one stay as ghost slabs for context; floors above are
 * hidden so the active plate is never occluded — unless stack mode is on,
 * in which case every level renders translucent.
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { FLOORS, ZONES, UNITS, AMENITIES, heatValue } from '../data/mallData';
import { amenitySymbolCanvas, VERTICAL_TYPES } from '../amenitySymbols';

/** Courts and amenity pins belong to the sample estate, not an imported plan. */

const UNIT_HEIGHT = 6;
const ANCHOR_HEIGHT = 7.6;
const SLAB_THICKNESS = 0.6;
const SLAB_W = 545;
const SLAB_D = 350;

const PALETTES = {
    dark: {
        bg: 0x070910,
        fog: 0x070910,
        slab: 0x232c3e,
        slabGhost: 0x141a26,
        corridor: 0x33405a,
        zone: 0x3b4a67,
        edge: 0x4a5a78,
        text: '#E2E8F0',
        textDim: '#64748B',
        grid: 0x1a2130,
        route: 0x38BDF8,
        ambient: 0.55,
        dir: 1.15,
    },
    light: {
        bg: 0xeef2f7,
        fog: 0xeef2f7,
        slab: 0xd7e0ea,
        slabGhost: 0xe8edf3,
        corridor: 0xf2f6fa,
        zone: 0xe1e9f3,
        edge: 0x8fa2b8,
        text: '#0F172A',
        textDim: '#64748B',
        grid: 0xcbd5e1,
        route: 0x0284C7,
        ambient: 0.85,
        dir: 1.05,
    },
};

/** Heat ramp: cool blue -> teal -> amber -> red. */
function rampColor(t) {
    const stops = [
        [0.0, [0x1E, 0x3A, 0x8A]],
        [0.35, [0x06, 0xB6, 0xD4]],
        [0.6, [0x84, 0xCC, 0x16]],
        [0.8, [0xF5, 0x9E, 0x0B]],
        [1.0, [0xEF, 0x44, 0x44]],
    ];
    const v = Math.max(0, Math.min(1, t));
    for (let i = 0; i < stops.length - 1; i++) {
        const [p0, c0] = stops[i];
        const [p1, c1] = stops[i + 1];
        if (v >= p0 && v <= p1) {
            const k = (v - p0) / (p1 - p0 || 1);
            return new THREE.Color(
                (c0[0] + (c1[0] - c0[0]) * k) / 255,
                (c0[1] + (c1[1] - c0[1]) * k) / 255,
                (c0[2] + (c1[2] - c0[2]) * k) / 255,
            );
        }
    }
    return new THREE.Color(0xEF4444);
}

function roundedRectShape(w, d, r) {
    const radius = Math.max(0, Math.min(r, Math.min(w, d) / 2 - 0.01));
    const x = -w / 2;
    const y = -d / 2;
    const s = new THREE.Shape();
    s.moveTo(x + radius, y);
    s.lineTo(x + w - radius, y);
    s.quadraticCurveTo(x + w, y, x + w, y + radius);
    s.lineTo(x + w, y + d - radius);
    s.quadraticCurveTo(x + w, y + d, x + w - radius, y + d);
    s.lineTo(x + radius, y + d);
    s.quadraticCurveTo(x, y + d, x, y + d - radius);
    s.lineTo(x, y + radius);
    s.quadraticCurveTo(x, y, x + radius, y);
    return s;
}

/**
 * Shape from an arbitrary mall-space polygon, expressed relative to `cx,cz`.
 *
 * The extrude path rotates the shape by -90 deg about X, which maps shape
 * (sx, sy) to world (sx, -sy) on the ground plane. So the z coordinate has to
 * be NEGATED going in, or every imported plan comes out mirrored north-south.
 */
function shapeFromPolygon(polygon, cx, cz) {
    const shape = new THREE.Shape();
    polygon.forEach(([px, pz], i) => {
        const sx = px - cx;
        const sy = -(pz - cz);
        if (i === 0) shape.moveTo(sx, sy);
        else shape.lineTo(sx, sy);
    });
    shape.closePath();
    return shape;
}

/** Point-in-polygon in mall space, for deciding what sits inside what. */
function polygonContains(polygon, x, z) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, zi] = polygon[i];
        const [xj, zj] = polygon[j];
        if (((zi > z) !== (zj > z))
            && (x < (((xj - xi) * (z - zi)) / ((zj - zi) || 1e-12)) + xi)) inside = !inside;
    }
    return inside;
}

/**
 * A floor plate with real openings cut out of it.
 *
 * An atrium is a HOLE, and drawing it as a dark disc lying on the mall is the
 * kind of detail that makes a model look like a diagram. THREE.Shape takes
 * holes directly, so cutting them properly costs nothing and lets you see
 * straight through to what is beneath.
 */
function plateShape(polygon, holes, cx, cz) {
    const shape = shapeFromPolygon(polygon, cx, cz);
    holes.forEach((hole) => {
        const path = new THREE.Path();
        hole.forEach(([px, pz], i) => {
            const sx = px - cx;
            const sy = -(pz - cz);
            if (i === 0) path.moveTo(sx, sy);
            else path.lineTo(sx, sy);
        });
        path.closePath();
        shape.holes.push(path);
    });
    return shape;
}

function roundRectPath(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

/**
 * Canvas-texture text sprite. Kept small; one per visible unit label.
 *
 * The label carries its OWN dark pill rather than taking the theme's text
 * colour. Textures are baked once at build time and setTheme() can't repaint
 * them, so a theme-coloured label goes unreadable the moment the user switches
 * — light-on-light. A self-contained chip reads on any slab, in either theme.
 */
/**
 * An amenity marker: a pictogram plaque on a post, over a disc on the floor.
 *
 * Three parts because each answers a different question. The DISC says where
 * exactly — it lies on the slab, so it reads as a place rather than a floating
 * object. The POST carries the eye up from it. The PLAQUE is the pictogram, and
 * it is a sprite with sizeAttenuation off, so it stays the same size on screen
 * at every zoom; a world-scaled symbol is either unreadable across the plate or
 * swallows the plan up close.
 *
 * Vertical circulation gets the warm accent, everything else the neutral: it is
 * the one family that changes what a route can DO, not just what it passes.
 */
function makeAmenityMarker(type, dpr) {
    const isCore = VERTICAL_TYPES.has(type);
    const accent = isCore ? 0x38BDF8 : 0x94A3B8;
    const accentCss = isCore ? '#38BDF8' : '#94A3B8';

    const canvas = amenitySymbolCanvas(type, 44, {
        dpr,
        color: '#F8FAFC',
        background: isCore ? 'rgba(12, 74, 110, 0.92)' : 'rgba(15, 23, 42, 0.9)',
        ring: accentCss,
    });
    const tex = new THREE.CanvasTexture(canvas);
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.anisotropy = 4;
    const spriteMat = new THREE.SpriteMaterial({
        map: tex, transparent: true, depthTest: false, depthWrite: false, sizeAttenuation: false,
    });
    const plaque = new THREE.Sprite(spriteMat);
    plaque.renderOrder = 21;

    const mat = new THREE.MeshStandardMaterial({
        color: accent, roughness: 0.4, metalness: 0.3, transparent: true, opacity: 1,
    });
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.35, 4.2, 8), mat);
    const disc = new THREE.Mesh(new THREE.CylinderGeometry(1.9, 1.9, 0.22, 24), mat);

    return { plaque, spriteMat, tex, post, disc, mat };
}

/**
 * The walkable grid as a texture on a plane just above the slab.
 *
 * One texture per floor rather than a mesh per cell: a 168 x 94 grid is ~16,000
 * cells, and 16,000 meshes would cost more than the entire rest of the scene to
 * draw something that is conceptually a single image.
 *
 * NEAREST filtering on purpose. The grid IS discrete — each cell is a routing
 * decision — so smoothing it would draw a soft edge where the router has a hard
 * one, and the gap you are looking for would be blurred out of view.
 */
function makeWalkableOverlay(grid, plateW, plateD) {
    const { w, h, cells } = grid;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    const img = ctx.createImageData(w, h);
    for (let i = 0; i < w * h; i++) {
        const on = !!cells[i];
        img.data[(i * 4) + 0] = 56;
        img.data[(i * 4) + 1] = 189;
        img.data[(i * 4) + 2] = 248;
        img.data[(i * 4) + 3] = on ? 150 : 0;
    }
    ctx.putImageData(img, 0, 0);

    const tex = new THREE.CanvasTexture(canvas);
    tex.magFilter = THREE.NearestFilter;
    tex.minFilter = THREE.NearestFilter;
    tex.generateMipmaps = false;
    const mat = new THREE.MeshBasicMaterial({
        map: tex, transparent: true, depthWrite: false, side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(plateW, plateD), mat);
    mesh.rotation.x = -Math.PI / 2;
    // Above the slab and above the surface fills, below the unit extrusions, so
    // it reads as painted on the floor rather than slicing through the shops.
    mesh.position.y = 0.34;
    mesh.renderOrder = 3;
    mesh.visible = false;
    return { mesh, mat, tex };
}

function makeLabelSprite(text, sub, dpr) {
    const pad = 12;
    const fontMain = 600;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = `${fontMain} 28px Inter, system-ui, sans-serif`;
    const wMain = ctx.measureText(text).width;
    ctx.font = '400 20px Inter, system-ui, sans-serif';
    const wSub = sub ? ctx.measureText(sub).width : 0;

    const w = Math.ceil(Math.max(wMain, wSub) + pad * 2);
    const h = sub ? 70 : 46;
    canvas.width = Math.ceil(w * dpr);
    canvas.height = Math.ceil(h * dpr);
    ctx.scale(dpr, dpr);

    roundRectPath(ctx, 1, 1, w - 2, h - 2, 10);
    ctx.fillStyle = 'rgba(9, 14, 26, 0.78)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.16)';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    ctx.textBaseline = 'top';
    ctx.fillStyle = '#F8FAFC';
    ctx.font = `${fontMain} 28px Inter, system-ui, sans-serif`;
    ctx.fillText(text, pad, sub ? 8 : 9);
    if (sub) {
        ctx.fillStyle = 'rgba(226, 232, 240, 0.72)';
        ctx.font = '400 20px Inter, system-ui, sans-serif';
        ctx.fillText(sub, pad, 42);
    }

    const tex = new THREE.CanvasTexture(canvas);
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.anisotropy = 4;
    // sizeAttenuation:false keeps the label a constant SCREEN size. With it on,
    // a world-space sprite balloons as you zoom in until the text swallows the
    // plan — the map equivalent of a font that scales with the paper.
    const mat = new THREE.SpriteMaterial({
        map: tex, transparent: true, depthTest: false, depthWrite: false, sizeAttenuation: false,
    });
    const sprite = new THREE.Sprite(mat);
    sprite.renderOrder = 20;
    sprite.userData.aspect = w / h;
    sprite.userData.lines = sub ? 2 : 1;
    return sprite;
}

export function createMallScene(container, opts = {}) {
    const {
        onSelect = () => {},
        onHover = () => {},
        theme = 'dark',
        // The unit set is injected so the same scene renders either the sample
        // estate or a traced floor plan. `showFurniture` hides the sample-only
        // courts and amenity pins, which mean nothing on an imported plan.
        units = UNITS,
        showFurniture = true,
        // Plate size in metres. An imported plan is whatever the calibration
        // said it was, so the slab and the camera framing both follow it.
        planSize = { w: SLAB_W, d: SLAB_D },
        // Amenity pins. The sample estate declares them; an imported plan
        // carries whatever the reviewer confirmed on the drawing.
        amenities = AMENITIES,
        // Everything the plan draws that is not leasable: promenades, atria,
        // cores, back-of-house, the apron outside. Geometry only — no lease, no
        // sales — but without it an imported plate reads as shops floating on
        // an empty slab rather than as a building.
        surfaces = [],
        /*
         * The navigable model the router walks, per floor id:
         *   { [floorId]: { w, h, cells: Uint8Array } }
         * Off by default — it is an authoring aid, not part of the map's
         * ordinary reading. See setWalkableVisible.
         */
        walkable = null,
        showWalkable = false,
    } = opts;

    let walkableVisible = !!showWalkable;

    const plateW = Math.max(60, planSize.w);
    const plateD = Math.max(60, planSize.d);
    const plateSpan = Math.max(plateW, plateD);

    let palette = PALETTES[theme] || PALETTES.dark;

    /**
     * How each non-leasable kind is drawn.
     *
     * `mix` blends the palette's own floor colour toward an accent, so surfaces
     * stay in the theme instead of punching fixed colours through it. `lift` is
     * the height above the slab; a void goes NEGATIVE so it reads as an opening
     * rather than as another plate.
     */
    const SURFACE_STYLE = {
        circulation: { base: 'corridor', accent: 0x22D3EE, mix: 0.1, thickness: 0.14, lift: 0.07, rough: 0.75 },
        amenity: { base: 'corridor', accent: 0x38BDF8, mix: 0.45, thickness: 1.1, lift: 0.1, rough: 0.5 },
        // A void reads as an opening through its colour, not by sitting below
        // the mall: it has to paint OVER the promenade plate that contains it.
        // Sits BELOW the mall plate and shows through the hole cut in it.
        void: { base: 'bg', accent: 0x000000, mix: 0.45, thickness: 0.05, lift: 0, rough: 0.95 },
        service: { base: 'slab', accent: 0x94A3B8, mix: 0.3, thickness: 3, lift: 0.1, rough: 0.85 },
        landscape: { base: 'zone', accent: 0x34D399, mix: 0.4, thickness: 0.25, lift: 0.09, rough: 0.9 },
        exterior: { base: 'slabGhost', accent: 0x000000, mix: 0.12, thickness: 0.05, lift: 0.02, rough: 1 },
    };

    const surfaceColor = (kind) => {
        const st = SURFACE_STYLE[kind] || SURFACE_STYLE.circulation;
        return new THREE.Color(palette[st.base]).lerp(new THREE.Color(st.accent), st.mix);
    };

    // ── renderer / camera / controls ────────────────────────────────────────
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(container.clientWidth || 800, container.clientHeight || 600);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(palette.bg);
    scene.fog = new THREE.Fog(palette.fog, plateSpan * 1.15, plateSpan * 2.3);

    const camera = new THREE.PerspectiveCamera(
        42,
        (container.clientWidth || 800) / (container.clientHeight || 600),
        1,
        2000,
    );
    camera.position.set(plateSpan * 0.48, plateSpan * 0.61, plateSpan * 0.66);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = Math.min(60, plateSpan * 0.12);
    controls.maxDistance = plateSpan * 1.8;
    controls.minPolarAngle = 0.12;
    controls.maxPolarAngle = Math.PI / 2.35;   // never go under the slab
    controls.screenSpacePanning = false;
    controls.target.set(0, 0, 0);

    // ── lighting ────────────────────────────────────────────────────────────
    const hemi = new THREE.HemisphereLight(0xdfe9ff, 0x0a0d14, palette.ambient);
    scene.add(hemi);

    const key = new THREE.DirectionalLight(0xffffff, palette.dir);
    key.position.set(140, 260, 120);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 10;
    key.shadow.camera.far = 800;
    key.shadow.camera.left = -280;
    key.shadow.camera.right = 280;
    key.shadow.camera.top = 280;
    key.shadow.camera.bottom = -280;
    key.shadow.bias = -0.0006;
    scene.add(key);

    const fill = new THREE.DirectionalLight(0x8ab4ff, 0.35);
    fill.position.set(-180, 120, -140);
    scene.add(fill);

    // ── state ───────────────────────────────────────────────────────────────
    let activeFloor = 'GF';
    let stackMode = false;
    let heatmapMode = 'none';
    let selectedId = null;
    let hoveredId = null;
    let visibleIds = null;      // Set of unit ids passing the filter, or null = all
    let labelsVisible = true;
    let disposed = false;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    /** floorId -> { group, slab, unitMeshes: Map<unitId, entry>, labels: [] } */
    const floorObjects = new Map();
    const pickables = [];       // meshes currently eligible for raycasting

    const routeGroup = new THREE.Group();
    routeGroup.renderOrder = 15;
    scene.add(routeGroup);

    // ── build the floor stack ───────────────────────────────────────────────

    const slabShape = roundedRectShape(plateW, plateD, Math.min(42, plateSpan * 0.08));

    function buildFloor(floor) {
        const group = new THREE.Group();
        group.position.y = floor.y;
        scene.add(group);

        // Slab
        const slabGeo = new THREE.ExtrudeGeometry(slabShape, { depth: SLAB_THICKNESS, bevelEnabled: false });
        slabGeo.rotateX(-Math.PI / 2);
        const slabMat = new THREE.MeshStandardMaterial({
            color: palette.slab, roughness: 0.92, metalness: 0.02, transparent: true, opacity: 1,
        });
        const slab = new THREE.Mesh(slabGeo, slabMat);
        slab.position.y = -SLAB_THICKNESS;
        slab.receiveShadow = true;
        group.add(slab);

        // Rim light on the plate edge — without it the slab dissolves into the
        // background at grazing angles and the mall loses its footprint.
        const slabEdgeGeo = new THREE.EdgesGeometry(slabGeo, 20);
        const slabEdgeMat = new THREE.LineBasicMaterial({
            color: palette.edge, transparent: true, opacity: 0.55,
        });
        const slabEdge = new THREE.LineSegments(slabEdgeGeo, slabEdgeMat);
        slabEdge.position.y = -SLAB_THICKNESS;
        group.add(slabEdge);

        // Corridor ribbons — the two boulevards, drawn as inset plates.
        const corridorMat = new THREE.MeshStandardMaterial({
            color: palette.corridor, roughness: 0.7, metalness: 0.05, transparent: true, opacity: 1,
        });
        const mainGeo = new THREE.BoxGeometry(Math.max(10, plateW - 40), 0.14, 22);
        const mainCorridor = new THREE.Mesh(mainGeo, corridorMat);
        mainCorridor.position.set(0, 0.07, 0);
        mainCorridor.receiveShadow = true;
        mainCorridor.visible = showFurniture;
        group.add(mainCorridor);

        const crossGeo = new THREE.BoxGeometry(22, 0.14, Math.max(10, plateD - 40));
        const crossCorridor = new THREE.Mesh(crossGeo, corridorMat);
        crossCorridor.position.set(0, 0.07, 0);
        crossCorridor.receiveShadow = true;
        crossCorridor.visible = showFurniture;
        group.add(crossCorridor);

        // Courts
        const zoneMat = new THREE.MeshStandardMaterial({
            color: palette.zone, roughness: 0.6, metalness: 0.08, transparent: true, opacity: 1,
        });
        const zoneMeshes = [];
        (showFurniture ? ZONES : []).forEach((zone) => {
            const geo = new THREE.CylinderGeometry(zone.r, zone.r, 0.22, 64);
            const mesh = new THREE.Mesh(geo, zoneMat);
            mesh.position.set(zone.x, 0.11, zone.z);
            mesh.receiveShadow = true;
            group.add(mesh);

            const ringGeo = new THREE.RingGeometry(zone.r - 1.1, zone.r, 64);
            const ringMat = new THREE.MeshBasicMaterial({
                color: new THREE.Color(zone.accent), transparent: true, opacity: 0.5, side: THREE.DoubleSide,
            });
            const ring = new THREE.Mesh(ringGeo, ringMat);
            ring.rotation.x = -Math.PI / 2;
            ring.position.set(zone.x, 0.24, zone.z);
            group.add(ring);
            zoneMeshes.push({ mesh, ring, ringMat });
        });

        // Units
        const unitMeshes = new Map();
        const labels = [];
        units.filter((u) => u.floor === floor.id).forEach((unit) => {
            const h = unit.isAnchor ? ANCHOR_HEIGHT : UNIT_HEIGHT;
            // Imported plans carry a traced outline; the demo estate is rectangular.
            const shape = unit.polygon && unit.polygon.length >= 3
                ? shapeFromPolygon(unit.polygon, unit.center.x, unit.center.z)
                : roundedRectShape(unit.geom.w, unit.geom.d, Math.min(2.2, Math.min(unit.geom.w, unit.geom.d) / 6));
            const geo = new THREE.ExtrudeGeometry(shape, { depth: h, bevelEnabled: false });
            geo.rotateX(-Math.PI / 2);

            const base = new THREE.Color(unit.color);
            const mat = new THREE.MeshStandardMaterial({
                color: base, roughness: 0.48, metalness: 0.12,
                transparent: true, opacity: 1,
                emissive: base.clone().multiplyScalar(0.06),
            });
            const mesh = new THREE.Mesh(geo, mat);
            mesh.position.set(unit.center.x, 0.2, unit.center.z);
            mesh.castShadow = true;
            mesh.receiveShadow = true;
            mesh.userData.unitId = unit.id;
            mesh.userData.floorId = floor.id;
            group.add(mesh);

            const edgeGeo = new THREE.EdgesGeometry(geo, 25);
            const edgeMat = new THREE.LineBasicMaterial({ color: palette.edge, transparent: true, opacity: 0.5 });
            const edges = new THREE.LineSegments(edgeGeo, edgeMat);
            edges.position.copy(mesh.position);
            group.add(edges);

            // Shopfront strip — a bright sliver on the corridor-facing edge, so
            // you can read at a glance which way a unit opens.
            const facingX = Math.abs(unit.frontage.x - unit.center.x) > Math.abs(unit.frontage.z - unit.center.z);
            const doorGeo = facingX
                ? new THREE.BoxGeometry(0.5, 0.5, Math.min(unit.geom.d * 0.55, 10))
                : new THREE.BoxGeometry(Math.min(unit.geom.w * 0.55, 10), 0.5, 0.5);
            const doorMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.42 });
            const door = new THREE.Mesh(doorGeo, doorMat);
            door.position.set(unit.frontage.x, 0.45, unit.frontage.z);
            group.add(door);

            unitMeshes.set(unit.id, { unit, mesh, mat, edges, edgeMat, door, doorMat, label: null, baseColor: base, height: h });
        });

        // Non-leasable surfaces, drawn first and low so units read on top of
        // them. They are deliberately NOT added to `pickables`: clicking the
        // mall floor should clear the selection, not select the floor.
        const surfaceMeshes = [];
        // Biggest first, each one a hair above the last.
        //
        // A traced outline is an OUTER contour: the promenade's polygon covers
        // the whole floor and the atrium inside it is a HOLE, not a cut-out. So
        // the shapes genuinely overlap, and without an explicit order the void
        // z-fights with the mall it sits in — or disappears under it.
        const ordered = surfaces
            .filter((sf) => sf.floor === floor.id && sf.polygon && sf.polygon.length >= 3)
            .slice()
            .sort((a, b) => (b.areaM2 || 0) - (a.areaM2 || 0));

        const voids = ordered.filter((sf) => sf.kind === 'void');

        ordered.forEach((sf, i) => {
            const st = SURFACE_STYLE[sf.kind] || SURFACE_STYLE.circulation;
            const stack = i * 0.006;
            // Cut every void that falls inside this plate out of it, so the
            // atrium is an opening rather than a lid.
            const holes = sf.kind === 'void' ? [] : voids
                .filter((v) => v !== sf && polygonContains(sf.polygon, v.center.x, v.center.z))
                .map((v) => v.polygon);
            const shape = plateShape(sf.polygon, holes, sf.center.x, sf.center.z);
            const geo = new THREE.ExtrudeGeometry(shape, { depth: st.thickness, bevelEnabled: false });
            geo.rotateX(-Math.PI / 2);
            const mat = new THREE.MeshStandardMaterial({
                color: surfaceColor(sf.kind),
                roughness: st.rough,
                metalness: 0.02,
                transparent: true,
                opacity: 1,
            });
            const mesh = new THREE.Mesh(geo, mat);
            mesh.position.set(sf.center.x, st.lift + stack, sf.center.z);
            mesh.receiveShadow = true;
            if (st.thickness > 1) mesh.castShadow = true;
            group.add(mesh);
            surfaceMeshes.push({ surface: sf, mesh, mat, kind: sf.kind });
        });

        // The navigable model, if this floor carries one.
        const grid = walkable && walkable[floor.id];
        const walkOverlay = grid ? makeWalkableOverlay(grid, plateW, plateD) : null;
        if (walkOverlay) {
            walkOverlay.mesh.visible = walkableVisible;
            group.add(walkOverlay.mesh);
        }

        // Amenity markers -- a pictogram per type, not an anonymous pin. See
        // makeAmenityMarker and ../amenitySymbols.
        const amenityPins = [];
        amenities.filter((a) => a.floors.includes(floor.id)).forEach((a) => {
            const m = makeAmenityMarker(a.type, dpr);
            m.disc.position.set(a.x, 0.16, a.z);
            m.disc.receiveShadow = true;
            group.add(m.disc);
            m.post.position.set(a.x, 2.3, a.z);
            m.post.castShadow = true;
            group.add(m.post);
            m.plaque.position.set(a.x, 5.6, a.z);
            group.add(m.plaque);
            amenityPins.push(m);
        });

        return { floor, group, slab, slabMat, slabEdge, slabEdgeMat, labelsBuilt: false, corridorMat, zoneMeshes, zoneMat, unitMeshes, surfaceMeshes, labels, amenityPins, walkOverlay, mainCorridor, crossCorridor };
    }

    FLOORS.forEach((f) => floorObjects.set(f.id, buildFloor(f)));

    // ── appearance passes ───────────────────────────────────────────────────

    /** Recompute per-floor visibility/opacity and rebuild the pick list. */
    function applyFloorVisibility() {
        pickables.length = 0;
        if (labelsVisible) ensureLabels(floorObjects.get(activeFloor));
        const activeLevel = (FLOORS.find((f) => f.id === activeFloor) || FLOORS[0]).level;

        FLOORS.forEach((f) => {
            const fo = floorObjects.get(f.id);
            const isActive = f.id === activeFloor;
            const below = f.level < activeLevel;
            const above = f.level > activeLevel;

            let show = true;
            let unitOpacity = 1;
            let slabOpacity = 1;

            if (stackMode) {
                unitOpacity = isActive ? 1 : 0.22;
                slabOpacity = isActive ? 1 : 0.3;
            } else if (isActive) {
                unitOpacity = 1;
                slabOpacity = 1;
            } else if (below) {
                unitOpacity = 0;          // ghost slab only, no shopfronts
                slabOpacity = 0.35;
            } else if (above) {
                show = false;
            }

            fo.group.visible = show;
            if (!show) return;

            fo.slabMat.opacity = slabOpacity;
            fo.slabMat.transparent = slabOpacity < 1;
            fo.slabEdgeMat.opacity = 0.55 * slabOpacity;
            fo.corridorMat.opacity = slabOpacity;
            fo.corridorMat.transparent = slabOpacity < 1;
            fo.zoneMat.opacity = slabOpacity;
            fo.zoneMat.transparent = slabOpacity < 1;
            fo.zoneMeshes.forEach((z) => { z.ringMat.opacity = 0.5 * slabOpacity; });

            // Surfaces are floor, so they follow the SLAB fade rather than the
            // unit fade — a ghosted level below should still read as a plate
            // with its promenades on it, not as bare slab.
            fo.surfaceMeshes.forEach((entry) => {
                entry.mat.opacity = slabOpacity;
                entry.mat.transparent = slabOpacity < 1;
                entry.mesh.visible = slabOpacity > 0.02;
            });
            fo.amenityPins.forEach((p) => {
                p.post.visible = unitOpacity > 0.5;
                p.disc.visible = unitOpacity > 0.5;
                p.plaque.visible = unitOpacity > 0.5;
                p.spriteMat.opacity = unitOpacity;
                p.mat.opacity = unitOpacity;
                p.mat.transparent = unitOpacity < 1;
            });

            fo.unitMeshes.forEach((entry) => {
                const filteredOut = visibleIds && !visibleIds.has(entry.unit.id);
                const o = filteredOut ? unitOpacity * 0.12 : unitOpacity;
                const visible = o > 0.02;

                entry.mesh.visible = visible;
                entry.edges.visible = visible;
                entry.door.visible = visible && !filteredOut;
                entry.mat.opacity = o;
                entry.mat.transparent = o < 1;
                entry.edgeMat.opacity = 0.5 * o;
                entry.doorMat.opacity = 0.42 * o;

                // `eligible` is the policy answer (right floor, passes the filter);
                // `visible` is the layout answer, decided per-frame by declutter().
                const eligible = labelsVisible && isActive && !filteredOut && unitOpacity > 0.5;
                if (entry.label) {
                    entry.label.userData.eligible = eligible;
                    if (!eligible) entry.label.visible = false;
                }

                if (isActive && !filteredOut && unitOpacity > 0.5) pickables.push(entry.mesh);
            });
        });
    }

    /** Colour pass: category tint, heatmap ramp, filter dimming, hover/select. */
    function applyColors() {
        const floorUnits = units.filter((u) => u.floor === activeFloor);

        floorObjects.forEach((fo, floorId) => {
            const isActive = floorId === activeFloor;
            fo.unitMeshes.forEach((entry) => {
                const { unit, mat, baseColor } = entry;
                let color = baseColor;

                if (heatmapMode !== 'none' && isActive) {
                    const t = heatValue(unit, heatmapMode, floorUnits);
                    color = t === null ? new THREE.Color(0x334155) : rampColor(t);
                } else if (unit.status === 'Vacant') {
                    color = new THREE.Color(0x475569);
                }

                const isSelected = unit.id === selectedId;
                const isHovered = unit.id === hoveredId;

                if (isSelected) {
                    mat.color.set(0xFACC15);
                    mat.emissive.setHex(0xFACC15).multiplyScalar(0.42);
                } else if (isHovered) {
                    mat.color.copy(color).lerp(new THREE.Color(0xffffff), 0.32);
                    mat.emissive.copy(color).multiplyScalar(0.3);
                } else {
                    mat.color.copy(color);
                    mat.emissive.copy(color).multiplyScalar(0.06);
                }

                // Selected + hovered units lift slightly off the slab.
                const targetY = isSelected ? 1.6 : isHovered ? 0.9 : 0.2;
                entry.mesh.userData.targetY = targetY;
                entry.edges.userData.targetY = targetY;
            });
        });
    }

    // ── label sizing + decluttering ─────────────────────────────────────────

    /**
     * Labels are built the first time a floor is shown, not at scene build.
     * Each is a canvas texture; making 400+ of them up front stalls the first
     * frame and pins GPU memory for text that may never be looked at.
     */
    function ensureLabels(fo) {
        if (!fo || fo.labelsBuilt) return;
        fo.labelsBuilt = true;
        fo.unitMeshes.forEach((entry) => {
            const label = makeLabelSprite(
                entry.unit.name,
                `${entry.unit.code} · ${entry.unit.gla.toLocaleString()} m²`,
                dpr,
            );
            label.position.set(entry.unit.center.x, entry.height + 4.5, entry.unit.center.z);
            label.userData.unitId = entry.unit.id;
            label.visible = false;
            fo.group.add(label);
            fo.labels.push(label);
            entry.label = label;
        });
        applyLabelScales();
    }

    /** On-screen height in CSS pixels for a label. Two-line labels get more. */
    const LABEL_PX = { 1: 19, 2: 29 };
    /** …and for an amenity plaque. Square, so this is both dimensions. */
    const AMENITY_PX = 26;

    /**
     * With sizeAttenuation off, a sprite's on-screen size is
     *   px = scale * (1 / tan(fov/2)) * viewportHeight / 2
     * so invert that to hit an exact pixel height. Depends on viewport height,
     * hence the recompute on resize.
     */
    function applyLabelScales() {
        const vh = container.clientHeight || 600;
        const k = 2 * Math.tan((camera.fov * Math.PI) / 180 / 2);
        floorObjects.forEach((fo) => {
            fo.labels.forEach((label) => {
                const px = LABEL_PX[label.userData.lines] || LABEL_PX[2];
                const sy = (px / vh) * k;
                label.scale.set(sy * label.userData.aspect, sy, 1);
                label.userData.pxH = px;
                label.userData.pxW = px * label.userData.aspect;
            });
            /*
             * Amenity plaques are sized by the SAME rule, and must be: a sprite
             * with sizeAttenuation off is scaled in viewport-height units, so the
             * three.js default of 1 means "as tall as the frame". Leaving it
             * unset does not make a marker slightly large, it makes one pictogram
             * swallow the whole map.
             */
            const ay = (AMENITY_PX / vh) * k;
            fo.amenityPins.forEach((p) => p.plaque.scale.set(ay, ay, 1));
        });
    }

    /**
     * Hide labels that would collide on screen, keeping the most important one.
     * Without this the shopfront runs turn into an unreadable pile of text at
     * anything but extreme zoom. Priority: selected, then hovered, then the
     * larger unit — the same order a cartographer would rank them.
     */
    const _projected = new THREE.Vector3();
    function declutterLabels() {
        const fo = floorObjects.get(activeFloor);
        if (!fo) return;
        const vw = container.clientWidth || 800;
        const vh = container.clientHeight || 600;

        const candidates = [];
        fo.unitMeshes.forEach((entry) => {
            const label = entry.label;
            if (!label || !label.userData.eligible) return;
            label.getWorldPosition(_projected);
            _projected.project(camera);
            if (_projected.z > 1) { label.visible = false; return; }   // behind the camera
            const sx = (_projected.x * 0.5 + 0.5) * vw;
            const sy = (-_projected.y * 0.5 + 0.5) * vh;
            candidates.push({
                label,
                sx,
                sy,
                w: label.userData.pxW,
                h: label.userData.pxH,
                rank: entry.unit.id === selectedId ? 1e9
                    : entry.unit.id === hoveredId ? 1e8
                        : entry.unit.gla,
            });
        });

        candidates.sort((a, b) => b.rank - a.rank);

        const kept = [];
        candidates.forEach((c) => {
            const l = c.sx - c.w / 2;
            const r = c.sx + c.w / 2;
            const t = c.sy - c.h / 2;
            const b = c.sy + c.h / 2;
            // Off-screen labels cost nothing to hide and can't be read anyway.
            if (r < 0 || l > vw || b < 0 || t > vh) { c.label.visible = false; return; }
            const clash = kept.some((k) => !(r < k.l - 2 || l > k.r + 2 || b < k.t - 2 || t > k.b + 2));
            c.label.visible = !clash;
            if (!clash) kept.push({ l, r, t, b });
        });
    }

    // ── route ribbon ────────────────────────────────────────────────────────

    let routeDashOffset = 0;
    let routeMaterials = [];

    function clearRoute() {
        while (routeGroup.children.length) {
            const c = routeGroup.children.pop();
            if (c.geometry) c.geometry.dispose();
            if (c.material) {
                if (c.material.map) c.material.map.dispose();
                c.material.dispose();
            }
        }
        routeMaterials = [];
    }

    function makeDashTexture() {
        const c = document.createElement('canvas');
        c.width = 64; c.height = 8;
        const ctx = c.getContext('2d');
        ctx.fillStyle = 'rgba(255,255,255,0.15)';
        ctx.fillRect(0, 0, 64, 8);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 34, 8);
        const tex = new THREE.CanvasTexture(c);
        tex.wrapS = THREE.RepeatWrapping;
        tex.wrapT = THREE.ClampToEdgeWrapping;
        tex.repeat.set(6, 1);
        return tex;
    }

    function setRoute(route) {
        clearRoute();
        if (!route) return;

        route.segments.forEach((seg) => {
            if (seg.points.length < 2) return;
            const floor = FLOORS.find((f) => f.id === seg.floor);
            if (!floor) return;
            const y = floor.y + 1.1;

            const pts = seg.points.map((p) => new THREE.Vector3(p.x, y, p.z));
            const curve = new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.02);
            const geo = new THREE.TubeGeometry(curve, Math.max(24, pts.length * 8), 1.35, 8, false);

            const tex = makeDashTexture();
            const len = curve.getLength();
            tex.repeat.set(Math.max(2, Math.round(len / 9)), 1);
            const mat = new THREE.MeshBasicMaterial({
                map: tex, color: palette.route, transparent: true, opacity: 0.95,
                depthTest: true, toneMapped: false,
            });
            const tube = new THREE.Mesh(geo, mat);
            tube.renderOrder = 16;
            routeGroup.add(tube);
            routeMaterials.push(mat);

            // Endpoint pucks
            const mk = (v, color) => {
                const m = new THREE.Mesh(
                    new THREE.CylinderGeometry(3.2, 3.2, 0.6, 32),
                    new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9, toneMapped: false }),
                );
                m.position.set(v.x, y - 0.4, v.z);
                m.renderOrder = 17;
                routeGroup.add(m);
            };
            mk(pts[0], seg === route.segments[0] ? 0x22C55E : 0xFACC15);
            mk(pts[pts.length - 1], seg === route.segments[route.segments.length - 1] ? 0xEF4444 : 0xFACC15);
        });
    }

    // ── interaction ─────────────────────────────────────────────────────────

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let downPos = null;

    function pointerToNDC(e) {
        const rect = renderer.domElement.getBoundingClientRect();
        pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    }

    function pick(e) {
        pointerToNDC(e);
        raycaster.setFromCamera(pointer, camera);
        const hits = raycaster.intersectObjects(pickables, false);
        return hits.length ? hits[0].object.userData.unitId : null;
    }

    function onPointerMove(e) {
        const id = pick(e);
        if (id !== hoveredId) {
            hoveredId = id;
            applyColors();
            renderer.domElement.style.cursor = id ? 'pointer' : 'grab';
            onHover(id, id ? { x: e.clientX, y: e.clientY } : null);
        } else if (id) {
            onHover(id, { x: e.clientX, y: e.clientY });
        }
    }

    function onPointerLeave() {
        if (hoveredId) {
            hoveredId = null;
            applyColors();
            onHover(null, null);
        }
    }

    function onPointerDown(e) { downPos = { x: e.clientX, y: e.clientY }; }

    function onPointerUp(e) {
        if (!downPos) return;
        const moved = Math.hypot(e.clientX - downPos.x, e.clientY - downPos.y);
        downPos = null;
        if (moved > 5) return;            // that was a drag, not a click
        onSelect(pick(e));
    }

    renderer.domElement.addEventListener('pointermove', onPointerMove);
    renderer.domElement.addEventListener('pointerleave', onPointerLeave);
    renderer.domElement.addEventListener('pointerdown', onPointerDown);
    renderer.domElement.addEventListener('pointerup', onPointerUp);
    renderer.domElement.style.cursor = 'grab';
    renderer.domElement.style.touchAction = 'none';

    // ── camera moves ────────────────────────────────────────────────────────

    let camAnim = null;

    function flyTo(targetVec, distance, duration = 700) {
        const dir = camera.position.clone().sub(controls.target).normalize();
        camAnim = {
            t: 0,
            duration,
            fromTarget: controls.target.clone(),
            toTarget: targetVec.clone(),
            fromPos: camera.position.clone(),
            toPos: targetVec.clone().add(dir.multiplyScalar(distance)),
        };
    }

    function frameFloor(floorId, duration = 700) {
        const f = FLOORS.find((x) => x.id === floorId) || FLOORS[0];
        flyTo(new THREE.Vector3(0, f.y, 0), plateSpan * 1.03, duration);
    }

    function focusUnit(unitId, duration = 750) {
        const unit = units.find((u) => u.id === unitId);
        if (!unit) return;
        const f = FLOORS.find((x) => x.id === unit.floor) || FLOORS[0];
        flyTo(new THREE.Vector3(unit.center.x, f.y + 6, unit.center.z), 165, duration);
    }

    function frameRoute(route, duration = 800) {
        if (!route || !route.segments.length) return;
        const box = new THREE.Box3();
        route.segments.forEach((seg) => {
            const f = FLOORS.find((x) => x.id === seg.floor);
            seg.points.forEach((p) => box.expandByPoint(new THREE.Vector3(p.x, f ? f.y : 0, p.z)));
        });
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const radius = Math.max(size.x, size.z, 40);
        flyTo(center, radius * 1.5 + 90, duration);
    }

    // ── loop ────────────────────────────────────────────────────────────────

    const clock = new THREE.Clock();
    let raf = 0;
    let declutterAccum = 0;

    function tick() {
        if (disposed) return;
        raf = requestAnimationFrame(tick);
        const dt = clock.getDelta();

        if (camAnim) {
            camAnim.t = Math.min(1, camAnim.t + (dt * 1000) / camAnim.duration);
            const e = 1 - Math.pow(1 - camAnim.t, 3);   // easeOutCubic
            controls.target.lerpVectors(camAnim.fromTarget, camAnim.toTarget, e);
            camera.position.lerpVectors(camAnim.fromPos, camAnim.toPos, e);
            if (camAnim.t >= 1) camAnim = null;
        }

        // Ease the hover/select lift.
        const activeFo = floorObjects.get(activeFloor);
        if (activeFo) {
            activeFo.unitMeshes.forEach((entry) => {
                const target = entry.mesh.userData.targetY;
                if (target === undefined) return;
                if (Math.abs(entry.mesh.position.y - target) > 0.005) {
                    entry.mesh.position.y += (target - entry.mesh.position.y) * Math.min(1, dt * 12);
                    entry.edges.position.y = entry.mesh.position.y;
                }
            });
        }

        // Crawl the route dashes toward the destination.
        if (routeMaterials.length) {
            routeDashOffset -= dt * 0.55;
            routeMaterials.forEach((m) => { if (m.map) m.map.offset.x = routeDashOffset; });
        }

        // Labels: fade the whole set out when zoomed far away, and re-run the
        // collision pass a few times a second (not every frame — it projects
        // every label and the camera barely moves between frames).
        if (labelsVisible && activeFo) {
            const d = camera.position.distanceTo(controls.target);
            // Fade band must sit BEYOND the default framing distance (560) or the
            // labels open at partial opacity on every fresh load.
            const farFade = plateSpan * 1.65;
            const nearFade = plateSpan * 1.32;
            const alpha = d > farFade ? 0 : d > nearFade ? 1 - (d - nearFade) / (farFade - nearFade) : 1;

            declutterAccum += dt;
            if (declutterAccum > 0.12) {
                declutterAccum = 0;
                if (alpha > 0) declutterLabels();
                else activeFo.labels.forEach((l) => { l.visible = false; });
            }

            activeFo.labels.forEach((l) => {
                if (!l.visible) return;
                l.material.opacity = alpha;
            });
        }

        controls.update();
        renderer.render(scene, camera);
    }

    function resize() {
        const w = container.clientWidth || 800;
        const h = container.clientHeight || 600;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
        applyLabelScales();   // label + plaque pixel size derive from viewport height
    }

    const ro = new ResizeObserver(resize);
    ro.observe(container);

    applyLabelScales();
    applyFloorVisibility();
    applyColors();
    frameFloor(activeFloor, 1);
    tick();

    // ── api ─────────────────────────────────────────────────────────────────
    return {
        setFloor(floorId, { fly = true } = {}) {
            if (floorId === activeFloor) return;
            activeFloor = floorId;
            hoveredId = null;
            applyFloorVisibility();
            applyColors();
            if (fly) frameFloor(floorId);
        },
        setStackMode(on) {
            stackMode = !!on;
            applyFloorVisibility();
            applyColors();
        },
        setHeatmap(mode) {
            heatmapMode = mode || 'none';
            applyColors();
        },
        setSelected(unitId) {
            selectedId = unitId;
            applyColors();
        },
        setVisibleUnits(ids) {
            visibleIds = ids ? new Set(ids) : null;
            applyFloorVisibility();
            applyColors();
        },
        setWalkableVisible(on) {
            walkableVisible = !!on;
            floorObjects.forEach((fo) => {
                if (fo.walkOverlay) fo.walkOverlay.mesh.visible = walkableVisible;
            });
        },
        setLabelsVisible(on) {
            labelsVisible = !!on;
            applyFloorVisibility();
        },
        setTheme(next) {
            palette = PALETTES[next] || PALETTES.dark;
            scene.background = new THREE.Color(palette.bg);
            scene.fog.color.setHex(palette.fog);
            hemi.intensity = palette.ambient;
            key.intensity = palette.dir;
            floorObjects.forEach((fo) => {
                fo.slabMat.color.setHex(palette.slab);
                fo.slabEdgeMat.color.setHex(palette.edge);
                fo.corridorMat.color.setHex(palette.corridor);
                fo.zoneMat.color.setHex(palette.zone);
                fo.unitMeshes.forEach((entry) => entry.edgeMat.color.setHex(palette.edge));
                // Surface colours are derived from the palette, so they have to
                // be recomputed rather than merely re-hexed.
                fo.surfaceMeshes.forEach((entry) => entry.mat.color.copy(surfaceColor(entry.kind)));
            });
            routeMaterials.forEach((m) => m.color.setHex(palette.route));
            applyColors();
        },
        setRoute,
        frameRoute,
        focusUnit,
        resetView() { frameFloor(activeFloor); },
        screenshot() {
            renderer.render(scene, camera);
            return renderer.domElement.toDataURL('image/png');
        },
        dispose() {
            disposed = true;
            cancelAnimationFrame(raf);
            ro.disconnect();
            renderer.domElement.removeEventListener('pointermove', onPointerMove);
            renderer.domElement.removeEventListener('pointerleave', onPointerLeave);
            renderer.domElement.removeEventListener('pointerdown', onPointerDown);
            renderer.domElement.removeEventListener('pointerup', onPointerUp);
            clearRoute();
            floorObjects.forEach((fo) => {
                fo.unitMeshes.forEach((entry) => {
                    entry.mesh.geometry.dispose();
                    entry.mat.dispose();
                    entry.edges.geometry.dispose();
                    entry.edgeMat.dispose();
                    entry.door.geometry.dispose();
                    entry.doorMat.dispose();
                    if (entry.label) {
                        if (entry.label.material.map) entry.label.material.map.dispose();
                        entry.label.material.dispose();
                    }
                });
                fo.zoneMeshes.forEach((z) => {
                    z.mesh.geometry.dispose();
                    z.ring.geometry.dispose();
                    z.ringMat.dispose();
                });
                if (fo.walkOverlay) {
                    fo.walkOverlay.mesh.geometry.dispose();
                    fo.walkOverlay.mat.dispose();
                    fo.walkOverlay.tex.dispose();
                }
                fo.amenityPins.forEach((p) => {
                    p.post.geometry.dispose();
                    p.disc.geometry.dispose();
                    p.mat.dispose();
                    p.tex.dispose();
                    p.spriteMat.dispose();
                });
                fo.slab.geometry.dispose();
                fo.slabMat.dispose();
                fo.slabEdge.geometry.dispose();
                fo.slabEdgeMat.dispose();
                fo.mainCorridor.geometry.dispose();
                fo.crossCorridor.geometry.dispose();
                fo.corridorMat.dispose();
                fo.zoneMat.dispose();
                scene.remove(fo.group);
            });
            controls.dispose();
            renderer.dispose();
            if (renderer.domElement.parentNode === container) container.removeChild(renderer.domElement);
        },
    };
}
